import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { UserAutocomplete } from './UserAutocomplete'
import { morphToast } from '@/components/ui/Toast'
import { ApiError } from '@/api/httpClient'
import { WorkspaceRoleValue, type User, type WorkspaceRole } from '@/api/types'

type RoleStringValue = 'OWNER' | 'ADMIN' | 'EMPLOYEE'

const ROLE_STRING_TO_NUM: Record<RoleStringValue, WorkspaceRole> = {
  OWNER: WorkspaceRoleValue.OWNER,
  ADMIN: WorkspaceRoleValue.ADMIN,
  EMPLOYEE: WorkspaceRoleValue.EMPLOYEE,
}

const NUM_TO_ROLE_STRING: Record<WorkspaceRole, RoleStringValue> = {
  1: 'OWNER',
  2: 'ADMIN',
  3: 'EMPLOYEE',
}

interface InviteMemberDialogProps {
  open: boolean
  onClose: () => void
  onInvite: (email: string, role: WorkspaceRole) => Promise<unknown>
  excludeUserIds?: number[]
  defaultRole?: WorkspaceRole
  canInviteOwner?: boolean
}

export function InviteMemberDialog({
  open,
  onClose,
  onInvite,
  excludeUserIds,
  defaultRole = WorkspaceRoleValue.EMPLOYEE,
  canInviteOwner = false,
}: InviteMemberDialogProps) {
  const { t } = useTranslation()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [emailQuery, setEmailQuery] = useState('')
  const [role, setRole] = useState<WorkspaceRole>(defaultRole)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedUser(null)
    setEmailQuery('')
    setRole(
      defaultRole === WorkspaceRoleValue.OWNER && !canInviteOwner
        ? WorkspaceRoleValue.EMPLOYEE
        : defaultRole,
    )
  }, [open, defaultRole, canInviteOwner])

  const trimmedEmail = emailQuery.trim()
  const canSubmit =
    trimmedEmail.length > 0 &&
    !submitting &&
    (role !== WorkspaceRoleValue.OWNER || canInviteOwner)

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await onInvite(trimmedEmail, role)
      morphToast.success(
        t('members.invite.success', 'Convite enviado para {{email}}', { email: trimmedEmail }),
      )
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        morphToast.fromError(err, t('common.error', 'Erro'))
      } else {
        morphToast.fromError(err, t('common.error', 'Erro'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const roleOptions = [
    ...(canInviteOwner
      ? [
          {
            value: 'OWNER' as const,
            label: t('members.role.owner', 'Owner'),
            description: t(
              'members.invite.roleHint.owner',
              'Acesso total, inclusive para administrar owners e o workspace.',
            ),
          },
        ]
      : []),
    {
      value: 'ADMIN' as const,
      label: t('members.role.admin', 'Admin'),
      description: t(
        'members.invite.roleHint.admin',
        'Gerencia membros e configurações do workspace.',
      ),
    },
    {
      value: 'EMPLOYEE' as const,
      label: t('members.role.employee', 'Colaborador'),
      description: t('members.invite.roleHint.employee', 'Cria projetos e colabora nos que é membro.'),
    },
  ]

  const currentRoleString = NUM_TO_ROLE_STRING[role]

  return (
    <Dialog
      open={open}
      title={t('members.invite.title', 'Convidar membro')}
      onClose={onClose}
      maxWidth={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {submitting
              ? t('members.invite.submitting', 'Enviando...')
              : t('members.invite.submit', 'Convidar')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
            {t('members.invite.emailLabel', 'Email')}
          </div>
          <UserAutocomplete
            value={selectedUser}
            query={emailQuery}
            onChange={(user, email) => {
              setSelectedUser(user)
              setEmailQuery(email)
            }}
            excludeUserIds={excludeUserIds}
            disabled={submitting}
            autoFocus
          />
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>
            {t(
              'members.invite.emailHint',
              'Digite qualquer e-mail. As sugestões opcionais mostram somente pessoas que já compartilham um workspace com você.',
            )}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
            {t('members.invite.roleLabel', 'Role')}
          </div>
          <MorphSelect<RoleStringValue>
            value={currentRoleString}
            onChange={(next) => {
              if (!next) return
              setRole(ROLE_STRING_TO_NUM[next])
            }}
            options={roleOptions}
            disabled={submitting}
            size="md"
            ariaLabel={t('members.invite.roleLabel', 'Role')}
          />
        </div>
      </div>
    </Dialog>
  )
}
